import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ArrowDownIcon } from "@heroicons/react/24/outline";
import ModernProjectSelector from "../Selector/ModernProjectSelector";
import { TWclassNames } from "../Div";
import { StringLimit } from "../../Utils";

/**
 * Modern Project Selector Field Component
 * 
 * A complete selector component that includes both the input field and dialog.
 * Can be used directly in forms, similar to ProjectSelector.
 * 
 * @param {Object} props
 * @param {string} props.name - Field name (default: "project_id")
 * @param {string} props.label - Label text
 * @param {Object} props.selected - Selected project object
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {Object} props.values - Formik values
 * @param {Object} props.errors - Formik errors
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {Function} props.onChange - Optional onChange callback
 * @param {Object} props.labelAction - Optional label action (e.g., link to project)
 * @param {boolean} props.showField - Whether to show the field (default: true)
 * @param {Array} props.filters - Additional filters to apply
 */
export const ModernProjectSelectorField = ({
  name = "project_id",
  label = "Project",
  selected,
  setFieldValue,
  values,
  errors,
  disabled = false,
  onChange,
  labelAction,
  showField = true,
  filters = [],
  /** Shown when nothing is selected (default: “Select project”) */
  emptyDisplayText,
  ...props
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(selected || null);

  // Update selectedProject when selected prop changes
  useEffect(() => {
    setSelectedProject(selected || null);
  }, [selected]);

  const handleChange = (selectedProjects) => {
    if (selectedProjects.length > 0) {
      const project = selectedProjects[0];
      setSelectedProject(project);
      
      if (setFieldValue) {
        setFieldValue(name, project.id?.toString() || project.id);
        // Also set project object if there's a field for it
        if (setFieldValue && name === "project_id") {
          setFieldValue("project", project);
        }
      }
      
      if (onChange) {
        onChange({
          target: {
            name: name,
            value: project.id,
            object: project,
          },
        });
      }
      
      setOpen(false);
    } else {
      setSelectedProject(null);
      if (setFieldValue) {
        setFieldValue(name, null);
        if (name === "project_id") {
          setFieldValue("project", null);
        }
      }
      setOpen(false);
    }
  };

  const displayText = selectedProject
    ? (selectedProject.displayTitle || selectedProject.name || t("Select project"))
    : emptyDisplayText || t("Select project");

  if (!showField) return null;

  return (
    <>
      <div>
        {label && (
          <label
            htmlFor={name}
            className="block text-sm font-medium text-gray-700"
          >
            {labelAction?.path ? (
              <a
                href={labelAction.path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 cursor-pointer hover:text-blue-600"
              >
                {t(label)}
              </a>
            ) : (
              t(label)
            )}
          </label>
        )}
        <div className="mt-1 focus-within:border-indigo-600">
          <div
            onClick={() => !disabled && setOpen(true)}
            className={TWclassNames(
              disabled
                ? "text-gray-400 cursor-not-allowed"
                : "cursor-pointer border-2 border-blue-400 hover:border-l-indigo-500",
              "relative py-1.5 px-1 bg-gray-50 shadow flex items-center rounded-md"
            )}
          >
            <span className="ml-2 flex">
              {displayText ? (
                <StringLimit text={displayText} limit={25} />
              ) : (
                emptyDisplayText || t("Select project")
              )}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
              <ArrowDownIcon className="h-3 w-3" aria-hidden="true" />
            </span>
          </div>
        </div>
      </div>

      {errors && errors[name] && (
        <div className="flex mt-1 text-red-700 text-xs">
          <span className="ml-2">{errors[name]}</span>
        </div>
      )}

      {/* Portal to document.body so the overlay is not clipped inside modals (e.g. PMDialog uses transform). */}
      {typeof document !== "undefined" &&
        open &&
        createPortal(
          <div className="fixed inset-0 z-[2600] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
              <div className="relative transform overflow-hidden rounded-none bg-white text-left shadow-xl transition-all sm:rounded-lg sm:my-8 w-full sm:max-w-2xl h-[80vh] sm:h-auto">
                <ModernProjectSelector
                  open={open}
                  selectedProjects={selectedProject ? [selectedProject].filter(Boolean) : []}
                  onChange={handleChange}
                  onClose={() => setOpen(false)}
                  multiple={false}
                  showHeader={true}
                  filters={filters}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

